import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { action, computed } from '@ember/object';
import { alias, readOnly, gte } from '@ember/object/computed';
import DomainModel from 'cio/models/domain';
import SetupGuide from 'cio/models/setup-guide';
import moment from 'moment-timezone';
import AnalyticsService from 'cio/services/analytics';
import DS from 'ember-data';
import SetupGuideModel from 'cio/models/setup-guide';
import { sortByCaseInsensitive } from 'cio/utils/sort';

export default class SetupGuideChannelsEmailController extends Controller {
  @service analytics!: AnalyticsService;
  @service store!: DS.Store;

  declare model: SetupGuideModel;

  selectedForAuthentication = '';
  selectedForLinkTracking = '';

  // "model" aliased to add typings without breaking computed properties observing it: Ember 3.13 regression
  // Bug: https://github.com/typed-ember/ember-cli-typescript/issues/1012#issuecomment-565821260
  // long-term fix is using the "declare" syntax introduced in TypeScript 3.7: https://www.typescriptlang.org/docs/handbook/release-notes/typescript-3-7.html#the-usedefineforclassfields-flag-and-the-declare-property-modifier
  // which is blocked until breaking ember-cli-typescript 4.0.0 release: https://github.com/typed-ember/ember-cli-typescript/issues/1033
  @alias('model') setupGuide!: SetupGuide;

  @readOnly('setupGuide.email.completedAt') isComplete = false;

  fetchIdentitiesTask = dropTask(async () => {
    return await this.store.query('identity', { type: 'email' });
  });

  fetchIdentitiesInUseTask = dropTask(async () => {
    return await this.store.query('identity', { in_use: true });
  });

  @computed('fetchIdentitiesTask.lastSuccessful.value')
  get identities() {
    return this.fetchIdentitiesTask.lastSuccessful?.value?.toArray() ?? [];
  }

  @gte('identities.length', 1) hasIdentities!: boolean;

  @computed('identities.@each.{name,emailDomain}')
  get sortedIdentities() {
    return this.identities ? sortByCaseInsensitive(this.identities, ['hasDynamicEmail', 'emailDomain', 'name']) : [];
  }

  @computed('fetchIdentitiesInUseTask.lastSuccessful.value')
  get identitiesInUse() {
    return this.fetchIdentitiesInUseTask.lastSuccessful?.value ?? [];
  }

  @computed('isComplete', 'domains.length', 'identities.length', 'anyDomainSetupComplete')
  get canCompleteStep() {
    if (this.isComplete) {
      return false;
    }

    if (this.domains.length === 0 || this.identities?.length === 0) {
      return false;
    }

    return this.anyDomainSetupComplete && this.universalLinksCompletedOrSkipped;
  }

  fetchDomainsTask = dropTask(async () => {
    return await this.store.findAll('domain');
  });

  @computed('fetchDomainsTask.lastSuccessful.value')
  get domains() {
    let domains = this.fetchDomainsTask.lastSuccessful?.value;
    return domains?.filter((domain) => !domain.isNew) ?? [];
  }

  @gte('domains.length', 1) hasDomains!: boolean;

  @computed(
    'domains.@each.{verifiedSpf,verifiedDkim,verified,verifiedHttps,verifiedHttp}',
    'setupGuide.email.skippedLinkTrackingForDomains.[]',
  )
  get anyDomainSetupComplete() {
    let {
      email: { skippedLinkTrackingForDomains },
    } = this.setupGuide;

    // If any domain has been verified and has skipped or set up link tracking,
    // the email channel can be marked complete.
    return this.domains.any((domain) => {
      let verified = domain.verified && domain.verifiedDkim && domain.verifiedSpf;
      let linkTrackingSetup = domain.verifiedHttps || domain.verifiedHttp;

      return verified && (linkTrackingSetup || skippedLinkTrackingForDomains.includes(domain.id));
    });
  }

  @computed('setupGuide.email.{completedUniversalLinkTrackingAt,skippedUniversalLinkTrackingAt}', 'setupGuide.push')
  get universalLinksCompletedOrSkipped() {
    // If there's no push set up, users don't need to see universal link tracking.
    if (this.setupGuide.push == null) {
      return true;
    }

    return (
      this.setupGuide.email.completedUniversalLinkTrackingAt != null ||
      this.setupGuide.email.skippedUniversalLinkTrackingAt != null
    );
  }

  @action
  fetchData() {
    this.fetchIdentitiesTask.perform();
    this.fetchIdentitiesInUseTask.perform();
    this.fetchDomainsTask.perform();
  }

  @action
  skipLinkTracking(domain: DomainModel) {
    let { email } = this.setupGuide;

    let copy = { ...email };
    if (copy.skippedLinkTrackingForDomains.includes(domain.id)) {
      copy.skippedLinkTrackingForDomains = copy.skippedLinkTrackingForDomains.filter((id) => id !== domain.id);
    } else {
      copy.skippedLinkTrackingForDomains = [...copy.skippedLinkTrackingForDomains, domain.id];
    }

    this.setupGuide.updateChannel(copy);

    this.analytics.track('onboarding', 'skip_clicked', { step: 'link_tracking' });
    return this.model.save();
  }

  @action
  onComplete() {
    this.setupGuide.updateChannel({
      ...this.setupGuide.email,
      completedAt: moment().unix(),
    });
    return this.setupGuide.save();
  }

  @action
  skipUniversalLinkTracking() {
    this.analytics.track('onboarding', 'skip_clicked', { step: 'universal_link_tracking' });
    this.setupGuide.updateChannel({
      ...this.setupGuide.email,
      skippedUniversalLinkTrackingAt: moment().unix(),
    });
    return this.model.save();
  }

  @action
  completeUniversalLinkTracking() {
    this.setupGuide.updateChannel({
      ...this.setupGuide.email,
      completedUniversalLinkTrackingAt: moment().unix(),
    });
    return this.setupGuide.save();
  }

  @action
  onSelectForAuthentication(domain: DomainModel | null) {
    this.set('selectedForAuthentication', domain?.domain ?? '');
  }

  @action
  onSelectForLinkTracking(domain: DomainModel | null) {
    this.set('selectedForLinkTracking', domain?.domain ?? '');
  }
}
